/**
 * Concierge store.
 *
 * Per-user history of Concierge requests, plus a personal FAQ of starred
 * requests. Persisted to localStorage keyed by user id so each person carries
 * their own shortcuts. Supabase sync can be layered on later; the contract
 * (record / toggleStar / load) would not change.
 */

import { create } from 'zustand';

export interface ConciergeEntry {
  id: string;
  /** The plain-language request the user typed. */
  query: string;
  /** The capability the router resolved it to, if any. */
  capabilityId: string | null;
  /** ms epoch. */
  ts: number;
}

const HISTORY_LIMIT = 30;
const FAQ_LIMIT = 50;

function storageKey(userId: string): string {
  return `pp_concierge_${userId || 'anon'}`;
}

interface Persisted {
  history: ConciergeEntry[];
  starred: ConciergeEntry[];
}

function load(userId: string): Persisted {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) return JSON.parse(raw) as Persisted;
  } catch {
    /* ignore */
  }
  return { history: [], starred: [] };
}

function save(userId: string, data: Persisted): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(data));
  } catch {
    /* ignore quota errors */
  }
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

interface ConciergeState {
  /** The user whose data is currently loaded. */
  userId: string | null;
  history: ConciergeEntry[];
  starred: ConciergeEntry[];

  /** Load (or switch to) a user's persisted history + FAQ. */
  hydrate: (userId: string) => void;
  /** Record a submitted request. De-duplicates by query (most-recent wins). */
  record: (userId: string, query: string, capabilityId: string | null) => void;
  /** Star/unstar a request into the personal FAQ (toggle by query text). */
  toggleStar: (userId: string, entry: ConciergeEntry) => void;
  isStarred: (query: string) => boolean;
  clearHistory: (userId: string) => void;
}

export const useConciergeStore = create<ConciergeState>((set, get) => ({
  userId: null,
  history: [],
  starred: [],

  hydrate: (userId) => {
    if (get().userId === userId) return;
    const data = load(userId);
    set({ userId, history: data.history, starred: data.starred });
  },

  record: (userId, query, capabilityId) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    const entry: ConciergeEntry = { id: newId(), query: trimmed, capabilityId, ts: Date.now() };
    const deduped = get().history.filter((h) => h.query.toLowerCase() !== trimmed.toLowerCase());
    const history = [entry, ...deduped].slice(0, HISTORY_LIMIT);
    set({ history });
    save(userId, { history, starred: get().starred });
  },

  toggleStar: (userId, entry) => {
    const key = entry.query.trim().toLowerCase();
    const exists = get().starred.some((s) => s.query.trim().toLowerCase() === key);
    const starred = exists
      ? get().starred.filter((s) => s.query.trim().toLowerCase() !== key)
      : [{ ...entry, id: newId(), ts: Date.now() }, ...get().starred].slice(0, FAQ_LIMIT);
    set({ starred });
    save(userId, { history: get().history, starred });
  },

  isStarred: (query) => {
    const key = query.trim().toLowerCase();
    return get().starred.some((s) => s.query.trim().toLowerCase() === key);
  },

  clearHistory: (userId) => {
    set({ history: [] });
    save(userId, { history: [], starred: get().starred });
  },
}));
